<template>
  <div
    class="management-table-page"
    :class="{ 'management-table-page--embedded': embedded }"
  >
    <div
      class="management-table-card"
      :class="{ 'management-table-card--embedded': embedded }"
    >
      <header v-if="!embedded" class="management-table-header">
        <div>
          <div class="management-table-eyebrow">{{ eyebrow }}</div>
          <h1>{{ title }}</h1>
          <p v-if="description" class="management-table-description">{{ description }}</p>
        </div>
        <slot name="header-actions" />
      </header>

      <div v-if="$slots.filters || $slots.actions" class="management-table-search">
        <button
          v-if="$slots.filters && mobileCollapsibleFilters"
          type="button"
          class="management-table-filter-toggle"
          :class="{ 'is-open': mobileFiltersOpen }"
          :aria-expanded="String(mobileFiltersOpen)"
          @click="mobileFiltersOpen = !mobileFiltersOpen"
        >
          <span><i class="el-icon-s-operation"></i>筛选条件</span>
          <i class="el-icon-arrow-down"></i>
        </button>
        <div class="management-table-search-row">
          <div
            v-if="$slots.filters"
            class="management-table-filter-fields"
            :class="{ 'is-mobile-collapsed': mobileCollapsibleFilters && !mobileFiltersOpen }"
          >
            <slot name="filters" />
          </div>
          <div v-if="$slots.actions" class="management-table-search-actions">
            <slot name="actions" />
          </div>
        </div>
      </div>

      <div class="management-table-panel">
        <div class="management-table-mobile-hint" aria-hidden="true">
          <i class="el-icon-d-caret"></i>
          <span>左右滑动查看完整表格</span>
        </div>
        <div
          ref="tableWrap"
          class="management-table-wrap"
          :style="tableVariables"
        >
          <el-table
            ref="table"
            v-loading="loading"
            v-bind="tableProps"
            :data="data"
            height="100%"
            style="width: 100%;"
            border
            :class="['management-table', tableClass]"
            :empty-text="emptyText"
            :row-style="getRowStyle"
            @selection-change="$emit('selection-change', $event)"
            @sort-change="$emit('sort-change', $event)"
            @row-click="$emit('row-click', $event)"
          >
            <slot name="columns" />
          </el-table>
        </div>

        <div class="management-table-pager">
          <el-pagination
            :current-page="currentPage"
            :page-size="pageSize"
            :page-sizes="pageSizes"
            :total="total"
            :layout="paginationLayout"
            :pager-count="isMobileViewport ? 5 : pagerCount"
            background
            @size-change="$emit('size-change', $event)"
            @current-change="$emit('current-change', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ManagementTable',
  props: {
    title: {
      type: String,
      required: true
    },
    eyebrow: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    },
    total: {
      type: Number,
      default: 0
    },
    emptyText: {
      type: String,
      default: '暂无数据'
    },
    tableClass: {
      type: String,
      default: ''
    },
    tableProps: {
      type: Object,
      default: () => ({})
    },
    paginationLayout: {
      type: String,
      default: 'prev, pager, next, jumper'
    },
    pageSizes: {
      type: Array,
      default: () => [10, 20, 50, 100]
    },
    pagerCount: {
      type: Number,
      default: 7
    },
    mobileCollapsibleFilters: {
      type: Boolean,
      default: true
    },
    mobileMinRowHeight: {
      type: Number,
      default: 56
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      rowHeight: 56,
      headerHeight: 44,
      scrollbarHeight: 0,
      resizeObserver: null,
      isMobileViewport: false,
      mobileFiltersOpen: false
    };
  },
  computed: {
    tableVariables() {
      return {
        '--management-row-height': this.rowHeight + 'px',
        '--management-header-height': this.headerHeight + 'px',
        '--management-scrollbar-height': this.scrollbarHeight + 'px'
      };
    }
  },
  watch: {
    data() {
      this.scheduleLayout();
    },
    pageSize() {
      this.scheduleLayout();
    }
  },
  mounted() {
    this.scheduleLayout();
    window.addEventListener('resize', this.scheduleLayout);
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.scheduleLayout);
      this.resizeObserver.observe(this.$refs.tableWrap);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.scheduleLayout);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  methods: {
    getRowStyle() {
      return {
        height: this.rowHeight + 'px'
      };
    },
    scheduleLayout() {
      this.$nextTick(this.updateLayout);
    },
    updateLayout() {
      const wrap = this.$refs.tableWrap;
      const table = this.$refs.table;
      const tableElement = table && table.$el;
      if (!wrap || !tableElement) return;

      if (table.doLayout) table.doLayout();

      const header = tableElement.querySelector('.el-table__header-wrapper');
      const body = tableElement.querySelector('.el-table__body-wrapper');
      const headerHeight = header ? header.offsetHeight : 44;
      const hasHorizontalScrollbar = body && body.scrollWidth > body.clientWidth;
      const scrollbarHeight = hasHorizontalScrollbar
        ? Math.max(0, body.offsetHeight - body.clientHeight)
        : 0;
      const visibleRows = this.pageSize || 1;
      const bodyHeight = Math.max(0, wrap.clientHeight - headerHeight - scrollbarHeight);

      this.isMobileViewport = window.innerWidth <= 760;
      this.headerHeight = headerHeight;
      this.scrollbarHeight = scrollbarHeight;
      this.rowHeight = this.isMobileViewport
        ? Math.max(this.mobileMinRowHeight, bodyHeight / visibleRows)
        : Math.max(1, bodyHeight / visibleRows);

      this.$nextTick(() => {
        if (this.$refs.table && this.$refs.table.doLayout) {
          this.$refs.table.doLayout();
        }
      });
    }
  }
};
</script>

<style>
.management-table-page {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(0, 153, 255, 0.08), transparent 34%), #001a33;
}

.management-table-card {
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(63, 184, 255, 0.45);
  border-radius: 8px;
  background: rgba(0, 31, 61, 0.92);
  box-shadow: 0 18px 40px rgba(0, 7, 18, 0.32), inset 0 1px 0 rgba(176, 226, 255, 0.18);
  box-sizing: border-box;
}

.management-table-page--embedded {
  align-items: stretch;
  justify-content: stretch;
  padding: 0;
  background: transparent;
}

.management-table-card--embedded {
  height: 100%;
  max-height: none;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.management-table-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px 14px;
  border-bottom: 1px solid rgba(63, 184, 255, 0.28);
  background: linear-gradient(90deg, rgba(0, 43, 77, 0.92), rgba(0, 26, 51, 0.72));
}

.management-table-eyebrow {
  color: #7bdff2;
  font-size: 12px;
  line-height: 18px;
  text-transform: uppercase;
}

.management-table-header h1 {
  margin: 2px 0 0;
  color: #effcff;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
}

.management-table-description {
  margin: 3px 0 0;
  color: #8fc9df;
  font-size: 13px;
  line-height: 18px;
}

.management-table-search {
  flex: 0 0 auto;
  width: 100%;
  padding: 14px 22px;
  border-bottom: 1px solid rgba(63, 184, 255, 0.2);
  background: rgba(0, 26, 51, 0.34);
  box-sizing: border-box;
}

.management-table-search-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  min-height: 36px;
}

.management-table-filter-fields {
  display: contents;
}

.management-table-filter-toggle,
.management-table-mobile-hint {
  display: none;
}

.management-table-page .search-item {
  flex: 0 1 260px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 220px;
  color: #9edcf4;
  font-size: 14px;
  line-height: 20px;
}

.management-table-page .search-label {
  flex: 0 0 auto;
  color: #9edcf4;
  font-size: 14px;
  white-space: nowrap;
}

.management-table-page .search-input,
.management-table-page .search-select {
  flex: 1 1 auto;
  width: auto !important;
  min-width: 0;
}

.management-table-search-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-left: auto;
}

.management-table-page .search-btn,
.management-table-page .add-btn {
  height: 36px;
  margin-left: 0 !important;
  border-radius: 4px;
  color: #002b4d !important;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}

.management-table-page .search-btn {
  min-width: 108px;
  background: #0099ff !important;
  border-color: #0099ff !important;
}

.management-table-page .add-btn {
  min-width: 122px;
  background: #00ccff !important;
  border-color: #00ccff !important;
}

.management-table-page .search-btn:hover,
.management-table-page .add-btn:hover {
  background: #0066cc !important;
  border-color: #0066cc !important;
}

.management-table-page .search-input.el-input,
.management-table-page .search-select.el-select {
  height: 36px;
  border: 1px solid rgba(63, 184, 255, 0.58) !important;
  border-radius: 4px;
  background: rgba(0, 18, 36, 0.72) !important;
  overflow: hidden;
  box-shadow: none !important;
  box-sizing: border-box;
}

.management-table-page .search-input > .el-input__inner,
.management-table-page .search-select .el-input > .el-input__inner {
  height: 36px !important;
  line-height: 36px !important;
  border: 0 !important;
  background: transparent !important;
  color: #dff8ff !important;
  box-shadow: none !important;
}

.management-table-panel {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px 22px 0;
  box-sizing: border-box;
  overflow: hidden;
}

.management-table-wrap {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid rgba(63, 184, 255, 0.28);
  border-radius: 8px;
  background: rgba(0, 18, 36, 0.42);
  box-shadow: 0 10px 22px rgba(0, 10, 24, 0.18);
}

.management-table-pager {
  flex: 0 0 auto;
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 0;
  background: rgba(0, 18, 36, 0.16);
  box-sizing: border-box;
}

.management-table.el-table {
  border: 0 !important;
  border-radius: 0;
  overflow: hidden;
}

.management-table.el-table > .el-loading-mask {
  background-color: rgba(0, 18, 36, 0.82) !important;
  backdrop-filter: blur(2px);
}

.management-table.el-table > .el-loading-mask .el-loading-spinner .path {
  stroke: #00ccff !important;
}

.management-table.el-table > .el-loading-mask .el-loading-spinner .el-loading-text {
  color: #9edcf4 !important;
}

.management-table.el-table::before,
.management-table.el-table::after,
.management-table .el-table__fixed-right::before,
.management-table .el-table__fixed::before {
  display: none !important;
}

.management-table .el-table__body-wrapper {
  height: calc(100% - var(--management-header-height)) !important;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  border-bottom: 0 !important;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 153, 255, 0.45) rgba(0, 26, 51, 0.25);
}

.management-table .el-table__body {
  border-bottom: 0 !important;
}

.management-table .el-table__body-wrapper::-webkit-scrollbar:horizontal {
  height: 8px;
}

.management-table .el-table__body-wrapper::-webkit-scrollbar-thumb:horizontal {
  background: linear-gradient(90deg, rgba(0, 153, 255, 0.2), rgba(0, 204, 255, 0.42), rgba(0, 153, 255, 0.2));
  border: 2px solid #001a33;
  border-radius: 999px;
}

.management-table .el-table__body-wrapper::-webkit-scrollbar-track:horizontal {
  background: #001a33;
}

.management-table .el-table__fixed-right {
  bottom: var(--management-scrollbar-height) !important;
  height: auto !important;
  border-bottom: 0 !important;
  background-color: #002b4d !important;
  box-shadow: -8px 0 18px rgba(0, 10, 24, 0.22);
}

.management-table-page--embedded .management-table .el-table__fixed {
  bottom: var(--management-scrollbar-height) !important;
  height: auto !important;
  border-bottom: 0 !important;
  background-color: #002b4d !important;
  box-shadow: 8px 0 18px rgba(0, 10, 24, 0.22);
}

.management-table-page--embedded .management-table .el-table__fixed .el-table__fixed-body-wrapper {
  top: var(--management-header-height) !important;
  bottom: 0 !important;
  height: auto !important;
  overflow: hidden !important;
}

.management-table .el-table__fixed-right .el-table__fixed-body-wrapper {
  top: var(--management-header-height) !important;
  bottom: 0 !important;
  height: auto !important;
  overflow: hidden !important;
}

.management-table .el-table__body tr,
.management-table .el-table__fixed-right .el-table__body tr {
  height: var(--management-row-height) !important;
}

.management-table .el-table__body tr td,
.management-table .el-table__fixed-right .el-table__body tr td {
  height: var(--management-row-height) !important;
  padding: 0 !important;
  border-bottom: 1px solid #003c66 !important;
  box-sizing: border-box;
}

.management-table .el-table__fixed-right .el-table__body-wrapper {
  height: calc(100% - var(--management-header-height)) !important;
  box-sizing: border-box;
}

.management-table .el-table__fixed-right-patch {
  height: var(--management-header-height) !important;
  background-color: #001a33 !important;
}

.management-table-page--embedded .management-table .el-table__fixed-patch {
  height: var(--management-header-height) !important;
  background-color: #001a33 !important;
}

.management-table .el-table__header th {
  height: var(--management-header-height);
  background: rgba(0, 26, 51, 0.88) !important;
  color: #dff8ff !important;
  font-weight: 700;
  text-align: center;
}

.management-table .el-table__body td,
.management-table .el-table__fixed-right .el-table__body td {
  background: rgba(0, 31, 61, 0.92) !important;
  color: #d9f5ff !important;
  text-align: center;
}

.management-table .el-table__body td .cell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 100%;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.management-table .el-table__body td .cell > span {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.management-table .el-table__body tr:hover > td,
.management-table .el-table__body tr.hover-row > td,
.management-table .el-table__fixed-right .el-table__body tr:hover > td,
.management-table .el-table__fixed-right .el-table__body tr.hover-row > td {
  background: rgba(0, 58, 94, 0.98) !important;
}

.management-table .el-table__empty-block {
  background: rgba(0, 31, 61, 0.84);
}

.management-table .el-table__empty-text {
  color: #89c8e4;
}

.management-table-pager .el-pagination,
.management-table-pager .el-pagination > span,
.management-table-pager .el-pagination > button,
.management-table-pager .el-pagination .el-pager {
  display: flex;
  align-items: center;
}

.management-table-pager .el-pagination {
  justify-content: flex-end;
  line-height: 1;
}

.management-table-pager .el-pagination > span,
.management-table-pager .el-pagination > button,
.management-table-pager .el-pagination .el-pager li,
.management-table-pager .el-pagination .el-pagination__jump,
.management-table-pager .el-pagination .el-pagination__editor,
.management-table-pager .el-pagination .el-input__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  line-height: 32px;
  box-sizing: border-box;
}

.management-table-pager .el-pagination > button {
  padding: 0;
}

.management-table-pager .el-pagination .el-pagination__editor,
.management-table-pager .el-pagination .el-pagination__editor.el-input,
.management-table-pager .el-pagination .el-pagination__editor .el-input,
.management-table-pager .el-pagination .el-pagination__sizes,
.management-table-pager .el-pagination .el-pagination__sizes .el-select,
.management-table-pager .el-pagination .el-pagination__sizes .el-input {
  height: 32px !important;
  line-height: 32px !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
  box-sizing: border-box !important;
}

.management-table-pager .el-pagination .el-pagination__editor .el-input__inner,
.management-table-pager .el-pagination .el-pagination__sizes .el-input__inner {
  height: 32px !important;
  min-height: 32px !important;
  line-height: 30px !important;
  border: 1px solid #0099ff !important;
  border-radius: 4px !important;
  background: #001a33 !important;
  color: #87cefa !important;
  box-shadow: none !important;
  box-sizing: border-box !important;
}

.management-table-pager .el-pagination .el-pagination__sizes .el-input__inner {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.management-table-pager .el-pagination.is-background .btn-next,
.management-table-pager .el-pagination.is-background .btn-prev,
.management-table-pager .el-pagination.is-background .el-pager li {
  background-color: rgba(0, 43, 77, 0.86);
  color: #9edcf4;
}

.management-table-pager .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #0a95d9;
  color: #fff;
}

@media (max-width: 760px) {
  .management-table-page {
    padding: 8px;
  }

  .management-table-card {
    border-radius: 7px;
  }

  .management-table-header {
    min-height: 58px;
    padding: 9px 12px 8px;
  }

  .management-table-eyebrow {
    font-size: 10px;
    line-height: 14px;
  }

  .management-table-header h1 {
    margin-top: 0;
    font-size: 18px;
    line-height: 26px;
  }

  .management-table-description {
    display: none;
  }

  .management-table-search {
    padding: 8px 10px 10px;
  }

  .management-table-filter-toggle {
    width: 100%;
    height: 36px;
    margin: 0 0 8px;
    padding: 0 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid rgba(63, 184, 255, 0.38);
    border-radius: 5px;
    background: rgba(0, 43, 77, 0.64);
    color: #bfeeff;
    font: inherit;
  }

  .management-table-filter-toggle span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  .management-table-filter-toggle > .el-icon-arrow-down {
    transition: transform 0.2s ease;
  }

  .management-table-filter-toggle.is-open > .el-icon-arrow-down {
    transform: rotate(180deg);
  }

  .management-table-search-row {
    display: block;
    min-height: 0;
  }

  .management-table-filter-fields {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
  }

  .management-table-filter-fields.is-mobile-collapsed {
    display: none;
  }

  .management-table-page .search-item {
    width: 100%;
    min-width: 0;
    flex: none;
  }

  .management-table-page .search-label {
    min-width: 70px;
  }

  .management-table-page .search-item > :last-child {
    min-width: 0;
    flex: 1 1 auto;
  }

  .management-table-search-actions {
    width: 100%;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 8px;
    margin-left: 0;
  }

  .management-table-page .search-btn,
  .management-table-page .add-btn {
    width: 100%;
    min-width: 0;
    padding: 0 8px;
  }

  .management-table-panel {
    padding: 0 10px;
  }

  .management-table-mobile-hint {
    flex: 0 0 28px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;
    color: #75bad5;
    font-size: 11px;
  }

  .management-table-mobile-hint .el-icon-d-caret {
    transform: rotate(90deg);
  }

  .management-table-wrap {
    border-radius: 6px;
  }

  .management-table .el-table__body-wrapper {
    overflow-x: auto !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-x pan-y;
  }

  .management-table .el-table__body-wrapper::-webkit-scrollbar:horizontal {
    height: 10px;
  }

  .management-table-pager {
    min-height: 46px;
    justify-content: center;
    padding: 7px 0;
  }

  .management-table-pager .el-pagination {
    max-width: 100%;
    justify-content: center;
    padding: 0;
  }

  .management-table-pager .el-pagination__jump {
    display: none !important;
  }

  .management-table-pager .el-pagination.is-background .btn-next,
  .management-table-pager .el-pagination.is-background .btn-prev,
  .management-table-pager .el-pagination.is-background .el-pager li {
    min-width: 28px;
    margin: 0 2px;
  }
}
</style>
